import { type EventOption, type InteractionEvent } from "../types";
import { activityFlowState, withDelayOption } from "./activityFlow";

export { categoryOf } from "./activityFlow";

/** An event option paired with a key that is unique within its list. */
export interface KeyedEventOption {
  key: string;
  opt: EventOption;
}

/**
 * Give each event option a key that is unique within the list, so pickers can
 * address one specific option.
 *
 * Event types are *not* unique: an airport's "Security Line", "Bag Drop" and
 * "Bag at Carousel" are all PROGRESS_UPDATE, and an airplane's "At Gate" and
 * "Doors Closed" likewise. Anything that identifies a chosen option by its
 * event type collapses those into a single choice and silently resolves to the
 * first one in the list. The index keeps the key unique even if two options
 * somehow carry the same id.
 */
export function keyEventOptions(options: EventOption[]): KeyedEventOption[] {
  return options.map((opt, index) => ({ key: `${index}:${opt.id}`, opt }));
}

/** Resolve the option a picker key refers to, or undefined if it's stale. */
export function findEventOptionByKey(
  keyed: KeyedEventOption[],
  key: string,
): EventOption | undefined {
  return keyed.find((k) => k.key === key)?.opt;
}

/**
 * The steps to put on buttons: whatever the entity's flow says can happen next
 * (see `utils/activityFlow`), plus the delay that can always interrupt it.
 *
 * Capped, because an entity is free to declare a step with a dozen successors
 * and a row of twelve buttons is no quicker to read than the full list. The
 * flow authors the order, so the ones that get cut are the least likely.
 */
const QUICK_ACTION_LIMIT = 6;

export function nextQuickOptions(
  options: EventOption[],
  events: InteractionEvent[],
): EventOption[] {
  const state = activityFlowState(options, events);
  return withDelayOption(state, options).slice(0, QUICK_ACTION_LIMIT);
}

/** What the quick-action row needs to draw itself. */
export interface QuickActions {
  /** The steps to offer, most likely first. */
  options: EventOption[];
  /** The flow offers a genuine choice here, not one obvious next step. */
  branching: boolean;
  /** A delay is open, so the only thing on offer is closing it. */
  delayOpen: boolean;
  /** Nothing follows — the activity is finished. */
  complete: boolean;
}

export function nextQuickActions(
  options: EventOption[],
  events: InteractionEvent[],
): QuickActions {
  const state = activityFlowState(options, events);
  return {
    options: withDelayOption(state, options).slice(0, QUICK_ACTION_LIMIT),
    branching: state.branching,
    delayOpen: state.delayOpen,
    complete: state.complete,
  };
}
